import { MCP_SPECIFICATION } from "@app/lib/actions/utils_ui";
import type { DataSourceViewCategory } from "@app/types/api/public/spaces";
import type { WhitelistableFeature } from "@app/types/shared/feature_flags";
import type { SpaceType } from "@app/types/space";
import {
  Building04,
  CloudArrowLeftRight,
  Cube01,
  CubeOutline,
  Folder,
  Globe01,
  Lock01,
  Server03,
  Terminal,
  Zap,
} from "@dust-tt/sparkle";
import i18n from "i18next";
import type React from "react";

export type { SpaceSectionGroupType } from "@app/lib/spaces_utils";
// Re-export non-UI utilities for backward compatibility.
export {
  dustAppsListUrl,
  getSpaceName,
  groupSpacesForDisplay,
  isPrivateSpacesLimitReached,
} from "@app/lib/spaces_utils";

export function getSpaceIcon(
  space: SpaceType
): (props: React.SVGProps<SVGSVGElement>) => React.ReactElement {
  if (space.kind === "project") {
    return space.isRestricted ? CubeOutline : Cube01;
  }

  if (space.isRestricted) {
    return Lock01;
  }

  if (space.kind === "global") {
    return Building04;
  }

  return Server03;
}

// `label` is a getter so every `.label` read resolves through i18n AT ACCESS
// TIME (render), not at module init — a plain string here would freeze the
// bootstrap language and keep these category names English in the Korean UI.
export const CATEGORY_DETAILS: {
  [key in DataSourceViewCategory]: {
    label: string;
    icon: React.ComponentType<{
      className?: string;
    }>;
    flag?: WhitelistableFeature;
  };
} = {
  managed: {
    get label() {
      return i18n.t("spaces.categoryManaged", "Connected Data");
    },
    icon: CloudArrowLeftRight,
  },
  folder: {
    get label() {
      return i18n.t("spaces.categoryFolder", "Folders");
    },
    icon: Folder,
  },
  website: {
    get label() {
      return i18n.t("spaces.categoryWebsite", "Websites");
    },
    icon: Globe01,
    // Webcrawler runs on Firecrawl; without a configured key a created website
    // never syncs (silent dead end) — hidden until the flag is set.
    flag: "webcrawler",
  },
  apps: {
    get label() {
      return i18n.t("spaces.categoryApps", "Apps");
    },
    icon: Terminal,
    flag: "legacy_dust_apps",
  },
  actions: {
    get label() {
      return i18n.t("spaces.categoryActions", "Tools");
    },
    icon: MCP_SPECIFICATION.cardIcon,
  },
  triggers: {
    get label() {
      return i18n.t("spaces.categoryTriggers", "Triggers");
    },
    icon: Zap,
  },
};
